import { Editor, useToast } from '@components/index'
import {
  RecipientGroup,
  useSendEmail
} from '@services/sendEmail/mutations/useSendEmail'
import { emailHistoryQueryKey } from '@services/sendEmail/queries/useEmailHistory'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Paperclip, Send, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

import { EmailAutocomplete } from './EmailAutocomplete'

const GROUPS: RecipientGroup[] = ['LANDLORD', 'USER', 'ONGOING_OFFER']

export function SendEmailForm() {
  const { t } = useTranslation('admin')
  const toast = useToast()
  const queryClient = useQueryClient()
  const { mutate: sendEmail, isPending } = useSendEmail()

  const [emails, setEmails] = useState<string[]>([])
  const [groups, setGroups] = useState<RecipientGroup[]>([])
  const [subject, setSubject] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Editor is uncontrolled (content read once at mount); bump to remount/clear it.
  const [editorKey, setEditorKey] = useState(0)
  const [errors, setErrors] = useState<{
    recipients?: string
    subject?: string
    title?: string
    message?: string
  }>({})

  const clearError = (key: keyof typeof errors) =>
    setErrors((prev) => ({ ...prev, [key]: undefined }))

  const updateEmails = (next: string[]) => {
    setEmails(next)
    if (next.length) clearError('recipients')
  }

  const toggleGroup = (group: RecipientGroup) => {
    const turningOn = !groups.includes(group)
    // Groups are mutually exclusive — only one can be selected at a time.
    setGroups((prev) => (prev.includes(group) ? [] : [group]))
    // Selecting any group hides the manual input — drop any chips so hidden
    // addresses are never sent silently.
    if (turningOn) setEmails([])
    clearError('recipients')
  }

  const hideManualEmails = groups.length > 0

  const handleSubmit = () => {
    const next: typeof errors = {}
    if (emails.length === 0 && groups.length === 0)
      next.recipients = t('sendEmail.validation.recipientsRequired')
    if (!subject.trim())
      next.subject = t('sendEmail.validation.subjectRequired')
    if (!title.trim()) next.title = t('sendEmail.validation.titleRequired')
    if (!body.trim() || body === '<p></p>')
      next.message = t('sendEmail.validation.messageRequired')
    if (Object.keys(next).length > 0) {
      setErrors(next)
      return
    }

    sendEmail(
      {
        recipient_emails: emails,
        groups,
        subject,
        title,
        message_text: body,
        attachments
      },
      {
        onSuccess: (data) => {
          toast.success({
            message:
              data?.sent != null
                ? t('sendEmail.successCount', { count: data.sent })
                : t('sendEmail.success')
          })
          setEmails([])
          setGroups([])
          setSubject('')
          setTitle('')
          setBody('')
          setAttachments([])
          setEditorKey((k) => k + 1)
          setErrors({})
          queryClient.invalidateQueries({ queryKey: emailHistoryQueryKey })
        }
      }
    )
  }

  return (
    <Card className="space-y-6 p-6">
        {/* Recipients */}
        <div>
          <Label className="mb-2 block">
            {t('sendEmail.labels.recipients')}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>

          <div className="flex flex-wrap gap-2">
            {GROUPS.map((group) => {
              const active = groups.includes(group)
              return (
                <button
                  key={group}
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
                    active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-ink/15 bg-white text-ink/70 hover:border-ink/30'
                  )}
                >
                  {active && <Check className="h-3.5 w-3.5" />}
                  {t(`sendEmail.groups.${group}`)}
                </button>
              )
            })}
          </div>

          {!hideManualEmails && (
            <div className="mt-3">
              <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                {t('sendEmail.labels.manualEmails')}
              </Label>
              <EmailAutocomplete
                value={emails}
                onChange={updateEmails}
                invalid={!!errors.recipients}
              />
            </div>
          )}

          {errors.recipients && (
            <p className="mt-1.5 text-sm font-medium text-destructive">
              {errors.recipients}
            </p>
          )}
        </div>

        {/* Subject */}
        <div>
          <Label className="mb-1.5 block">
            {t('sendEmail.labels.subject')}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <Input
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value)
              clearError('subject')
            }}
            className={errors.subject ? 'border-destructive' : ''}
          />
          {errors.subject && (
            <p className="mt-1.5 text-sm font-medium text-destructive">
              {errors.subject}
            </p>
          )}
        </div>

        {/* Title */}
        <div>
          <Label className="mb-1.5 block">
            {t('sendEmail.labels.title')}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <Input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value)
              clearError('title')
            }}
            className={errors.title ? 'border-destructive' : ''}
          />
          {errors.title && (
            <p className="mt-1.5 text-sm font-medium text-destructive">
              {errors.title}
            </p>
          )}
        </div>

        {/* Message */}
        <div>
          <Label className="mb-1.5 block">
            {t('sendEmail.labels.message')}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <Editor
            key={editorKey}
            content={body}
            onUpdate={(value: string) => {
              setBody(value)
              clearError('message')
            }}
          />
          {errors.message && (
            <p className="mt-1.5 text-sm font-medium text-destructive">
              {errors.message}
            </p>
          )}
        </div>

        {/* Attachments */}
        <div>
          <Label className="mb-1.5 block">
            {t('sendEmail.labels.attachments')}
          </Label>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length) setAttachments((prev) => [...prev, ...files])
              e.target.value = ''
            }}
          />
          <Button
            type="button"
            variant="ghostDark"
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="h-4 w-4" />
            {t('sendEmail.labels.attachments')}
          </Button>
          {attachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {attachments.map((file, i) => (
                <span
                  key={`${file.name}-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/[0.08] bg-ink/[0.03] py-1 pl-3 pr-1.5 text-sm text-ink"
                >
                  <span className="max-w-[200px] truncate">{file.name}</span>
                  <button
                    type="button"
                    aria-label="remove"
                    onClick={() =>
                      setAttachments((prev) => prev.filter((_, j) => j !== i))
                    }
                    className="flex h-5 w-5 items-center justify-center rounded-full border-0 bg-transparent p-0 text-muted-foreground transition-colors hover:bg-ink/[0.06] hover:text-ink"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={isPending}>
            <Send className="h-4 w-4" />
            {t('sendEmail.submit')}
          </Button>
        </div>
      </Card>
  )
}
